<template>
  <div id="form" :class="{ wizard: isWizard }" class="form-group">
    <h4>
      <span>{{ editId != null ? "Edit" : "Add" }} Traveler</span>
      <span v-if="editId != null && !isWizard"> &middot; <a @click="reset">Cancel</a></span>
    </h4>
    <p class="infomsg">Please ensure the required information below is exactly the same as recorded on your passport. Most importantly, do not include shortened names, unless they also appear on your passport Any discrepancies can lead to the airline denying boarding.</p>
    <Result v-model:error="formError" v-model:is-success="formIsSuccess" />
    <div :class="{ loading: formIsLoading }">
      <div class="form-section">
        <h4>Required Information</h4>
        <div class="form-row">
          <div class="form-col">
            <input v-model="formPassportFirstName" type="text" maxlength="25" required placeholder="First name" />
          </div>
          <div class="form-col">
            <input v-model="formPassportMiddleName" type="text" maxlength="25" placeholder="Middle Name/s" />
          </div>
          <div class="form-col">
            <input v-model="formPassportLastName" type="text" maxlength="50" required placeholder="Last Name" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-col">
            <input v-model="formEmail" type="text" maxlength="100" required placeholder="Email (for check-in emails)" />
          </div>
          <div class="form-col">
            <input v-model="formPhoneNumber" type="text" maxlength="30" required placeholder="Phone Number" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-row form-col">
            <div class="form-col">
              <select v-model="formDateOfBirthYear" class="year" required>
                <option value="" disabled>DOB Yr</option>
                <option v-for="year in formYears(true, 100)" :key="year" :value="year">
                  {{ year }}
                </option>
              </select>
            </div>
            <div class="form-col">
              <select v-model="formDateOfBirthMonth" class="month" required>
                <option value="" disabled>DOB Mth</option>
                <option v-for="n in 12" :key="n" :value="n">
                  {{ n < 10 ? "0" + n : n }}
                </option>
              </select>
            </div>
            <div class="form-col">
              <select v-model="formDateOfBirthDay" class="day" required>
                <option value="" disabled>DOB Day</option>
                <option v-for="day in 31" :key="day" :value="day">
                  {{ day }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-col">
            <select v-model="formSex" required>
              <option value="" disabled>Sex</option>
              <option value="f">Female</option>
              <option value="m">Male</option>
            </select>
          </div>
        </div>
      </div>
      <div class="form-section">
        <h4>Optional Information</h4>
        <div class="form-row">
          <div class="form-col">
            <input v-model="formPassportNumber" type="text" maxlength="100" placeholder="Passport Number" />
          </div>
          <div class="form-col">
            <select v-model="formPassportCountry" required>
              <option :value="null" disabled>Passport Country</option>
              <option v-for="country in formCountries" :key="country.id" :value="country.id">
                {{ country.title }}
              </option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-row form-col">
            <div class="form-col">
              <select v-model="formPassportExpiryYear" class="year" required>
                <option :value="null" disabled>Exp Yr</option>
                <option v-for="year in formYears(false, 10)" :key="year" :value="year">
                  {{ year }}
                </option>
              </select>
            </div>
            <div class="form-col">
              <select v-model="formPassportExpiryMonth" class="month" required>
                <option value="" disabled>Exp Mth</option>
                <option v-for="n in 12" :key="n" :value="n">
                  {{ n < 10 ? "0" + n : n }}
                </option>
              </select>
            </div>
            <div class="form-col">
              <select v-model="formPassportExpiryDay" class="day" required>
                <option value="" disabled>Exp Day</option>
                <option v-for="day in 31" :key="day" :value="day">
                  {{ day }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-col">
            <select v-model="formSeatPreference" required>
              <option :value="null">No Seat Preference</option>
              <option :value="false">Aisle</option>
              <option :value="true">Window</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-col">
            <input v-model="formDietaryRequirements" type="text" maxlength="255" placeholder="Dietary Requirements" />
          </div>
          <div class="form-col">
            <input v-model="formOther" type="text" maxlength="100" placeholder="Redress/KTN/Other" />
          </div>
        </div>
      </div>
      <div class="form-section">
        <h4>Loyalty Programs</h4>
        <div v-for="(travelerProgram, index) in formTravelerPrograms" :key="index" class="form-row program">
          <div class="form-col">
            <select v-model="travelerProgram.program_id" required>
              <option value="" disabled>Program Name</option>
              <optgroup v-for="(programs, group) in formPrograms" :key="group" :label="group">
                <option v-for="program in programs" :key="program.id" :value="program.id">
                  {{ program.title }}
                </option>
              </optgroup>
            </select>
          </div>
          <div class="form-col form-col-action">
            <input v-model="travelerProgram.number" type="text" placeholder="Account Number" />
            <a v-if="formTravelerPrograms.length > 1" class="remove" @click="travelerProgramsRemove(index)">&cross;</a>
          </div>
        </div>
        <a @click="travelerProgramsAdd">Add Program</a>
      </div>
      <div id="navigation">
        <button @click="save">Save Traveler</button>
        <a v-if="isWizard && isNoPax" @click="actionSkip()">Skip this Step</a>
        <a v-if="isWizard" @click="isNoPax ? actionBack() : actionCancel()">Back</a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, inject } from "vue";
import Result from "../../components/common/FormResult.vue";
import useApi from "../../utils/apiCalls.js";

const userId = inject("userId");

const props = defineProps({
  editId: [String, Number, null],
  newId: [String, Number, null],
  isNoPax: Boolean,
  isWizard: Boolean,
});

const emit = defineEmits(["back", "error", "next", "update:editId", "update:isForm", "update:newId"]);
const { apiGetRequest, apiPostRequest, apiPutRequest } = useApi(emit);

const formPassportFirstName = ref(null);
const formPassportMiddleName = ref(null);
const formPassportLastName = ref(null);
const formEmail = ref(null);
const formPhoneNumber = ref(null);
const formDateOfBirthYear = ref("");
const formDateOfBirthMonth = ref("");
const formDateOfBirthDay = ref("");
const formSex = ref("");
const formPassportNumber = ref(null);
const formPassportCountry = ref(null);
const formPassportExpiryYear = ref(null);
const formPassportExpiryMonth = ref("");
const formPassportExpiryDay = ref("");
const formSeatPreference = ref(null);
const formDietaryRequirements = ref(null);
const formOther = ref(null);
const formTravelerPrograms = ref([{ program_id: "", number: null }]);
const formIsLoading = ref(false);
const formIsSuccess = ref(false);
const formError = ref(null);
const formCountries = ref([]);
const formPrograms = ref({});

const formDateOfBirth = computed({
  get() {
    if (!formDateOfBirthYear.value || !formDateOfBirthMonth.value || !formDateOfBirthDay.value) {
      return null;
    }
    return new Date(Date.UTC(formDateOfBirthYear.value, formDateOfBirthMonth.value - 1, formDateOfBirthDay.value, 0, 0, 0));
  },
  set(val) {
    formDateOfBirthYear.value = val.getUTCFullYear();
    formDateOfBirthMonth.value = val.getUTCMonth() + 1;
    formDateOfBirthDay.value = val.getUTCDate();
  },
});

const formPassportExpiry = computed({
  get() {
    if (!formPassportExpiryYear.value || !formPassportExpiryMonth.value || !formPassportExpiryDay.value) {
      return null;
    }
    return new Date(Date.UTC(formPassportExpiryYear.value, formPassportExpiryMonth.value - 1, formPassportExpiryDay.value, 0, 0, 0));
  },
  set(val) {
    formPassportExpiryYear.value = val != null ? val.getUTCFullYear() : null;
    formPassportExpiryMonth.value = val != null ? val.getUTCMonth() + 1 : null;
    formPassportExpiryDay.value = val != null ? val.getUTCDate() : null;
  },
});

const actionBack = () => {
  emit("back");
};
const actionCancel = () => {
  emit("update:isForm", false);
};
const actionSkip = () => {
  emit("next");
};

const formYears = (is_reverse, limit) => {
  var i;
  var list = [];
  const year = new Date().getFullYear();
  if (is_reverse) {
    for (i = year; i >= year - limit; i--) {
      list.push(i);
    }
  } else {
    for (i = year; i <= year + limit; i++) {
      list.push(i);
    }
  }
  return list;
};

const load = () => {
  formError.value = null;
  formIsLoading.value = true;

  apiGetRequest({
    path: `/travelers/${props.editId}`,
    onSuccess: data => {
      formPassportFirstName.value = data.passport_first_name;
      formPassportMiddleName.value = data.passport_middle_name;
      formPassportLastName.value = data.passport_last_name;
      formEmail.value = data.email;
      formPhoneNumber.value = data.phone_number;
      formDateOfBirth.value = new Date(data.date_of_birth);
      formSex.value = data.sex;
      formPassportNumber.value = data.passport_number;
      formPassportCountry.value = data.passport_country_id;
      formPassportExpiry.value = data.passport_expiry != null ? new Date(data.passport_expiry) : null;
      formSeatPreference.value = data.seat_preference;
      formDietaryRequirements.value = data.dietary_requirements;
      formOther.value = data.other;
      formTravelerPrograms.value = data.traveler_programs;

      if (formTravelerPrograms.value.length == 0) {
        travelerProgramsAdd();
      }

      formIsLoading.value = false;
    },
    onError: error => {
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};

const loadCountries = () => {
  if (formCountries.value.length > 0) return;
  apiGetRequest({
    path: "/countries",
    onSuccess: data => {
      formCountries.value = data;
      formIsLoading.value = false;
    },
    onError: error => {
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};

const loadPrograms = () => {
  if (Object.keys(formPrograms.value).length > 0) return;
  apiGetRequest({
    path: "/programs",
    onSuccess: data => {
      var programsGrouped = data.reduce(function (h, obj) {
        h[obj.group] = (h[obj.group] || []).concat(obj);
        return h;
      }, {});
      formPrograms.value = programsGrouped;
      formIsLoading.value = false;
    },
    onError: error => {
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};

const reset = () => {
  emit("update:editId", null);
  formPassportFirstName.value = null;
  formPassportMiddleName.value = null;
  formPassportLastName.value = null;
  formEmail.value = null;
  formPhoneNumber.value = null;
  formDateOfBirthYear.value = "";
  formDateOfBirthMonth.value = "";
  formDateOfBirthDay.value = "";
  formSex.value = "";
  formPassportNumber.value = null;
  formPassportCountry.value = null;
  formPassportExpiryYear.value = null;
  formPassportExpiryMonth.value = "";
  formPassportExpiryDay.value = "";
  formSeatPreference.value = null;
  formDietaryRequirements.value = null;
  formOther.value = null;
  formTravelerPrograms.value = [{ program_id: "", number: null }];
  formIsLoading.value = false;
  formIsSuccess.value = false;
  formError.value = null;
};

const save = () => {
  formIsLoading.value = true;

  const apiRequest = props.editId != null ? apiPutRequest : apiPostRequest;
  const apiPath = props.editId != null ? `/travelers/${props.editId}` : `/travelers`;

  apiRequest({
    path: apiPath,
    data: {
      customer_id: Number(userId.value),
      passport_first_name: formPassportFirstName.value,
      passport_middle_name: formPassportMiddleName.value,
      passport_last_name: formPassportLastName.value,
      email: formEmail.value,
      phone_number: formPhoneNumber.value,
      date_of_birth: formDateOfBirth.value,
      sex: formSex.value ? formSex.value : null,
      passport_number: formPassportNumber.value,
      passport_country_id: formPassportNumber.value && formPassportCountry.value ? formPassportCountry.value : null,
      passport_expiry: formPassportNumber.value ? formPassportExpiry.value : null,
      seat_preference: formSeatPreference.value && formSeatPreference.value !== "" ? formSeatPreference.value : null,
      dietary_requirements: formDietaryRequirements.value,
      other: formOther.value,
      traveler_programs: travelerProgramsPrepare(),
    },
    onSuccess: data => {
      reset();
      if (!props.isWizard) {
        formIsSuccess.value = true;
      }
      loadCountries();
      loadPrograms();
      formIsLoading.value = false;
      if (props.editId != null) {
        emit("update:editId", null);
      } else {
        emit("update:newId", data);
      }
    },
    onError: error => {
      formIsSuccess.value = false;
      formError.value = error;
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};

const travelerProgramsAdd = () => {
  formTravelerPrograms.value.push({ program_id: 0, number: "" });
};

const travelerProgramsRemove = index => {
  formTravelerPrograms.value.splice(index, 1);
  if (formTravelerPrograms.value.length == 0) {
    // To add a row back to the UI
    travelerProgramsAdd();
  }
};

const travelerProgramsPrepare = () => {
  var validated = [];
  for (let i = 0; i < formTravelerPrograms.value.length; i++) {
    var program = formTravelerPrograms.value[i];
    if (program.program_id !== undefined && program.program_id != 0) {
      validated.push({
        program_id: program.program_id,
        number: program.number,
      });
    }
  }
  return validated;
};

watch(
  () => props.editId,
  () => {
    if (props.editId != null) {
      var el = document.getElementById("form");
      el.scrollIntoView({ behavior: "smooth" });
      load();
    }
  }
);

onMounted(() => {
  loadCountries();
  loadPrograms();

  if (props.editId != null) {
    load();
  }
});
</script>

<style lang="less">
#form {
  .search {
    margin: 0;
  }

  .info {
    color: @dark;
    margin-bottom: 1rem;
  }
}
</style>
